import '../../styles/style.css'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ApiCaller } from '@frontend/api'
import type { ArtifactInfos, ChatMessage } from '@types'
import { useChatContext } from '@ui/chat/ChatProvider'
import { useArtifactSocket, useSentry } from '@utils/frontend'
import {
	createContext, useContext, useEffect, useState
} from 'react'

import Content from './Artifact.Content'
import Footer from './Artifact.Footer'

import type { ReactPdfViewerParams } from '../ReactPdfViewer'

import { useAppContext, useGlobalContext } from '~/contexts'

export interface ArtifactParams {
	readonly artifact: ArtifactInfos;
	readonly artifactMessage: ChatMessage;
}

interface ArtifactProps {
	readonly artifactParams: ArtifactParams;
	readonly chatId: string;
	readonly children: React.ReactNode | React.ReactNode[];
}

interface IArtifactContext {
	readonly artifactParams: ArtifactParams;
	readonly isProcessingResponse: boolean;
	readonly artifactContent: string;
	readonly onSourceSelected: (params: ReactPdfViewerParams | undefined) => void;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const ArtifactContext = createContext<IArtifactContext | undefined>(undefined)

export default function Artifact({
	children, chatId, artifactParams
}: ArtifactProps) {
	const [isClosing, setIsClosing] = useState(false)
	const { captureException } = useSentry()
	const { chatState, chatDispatch } = useChatContext()
	const isProcessingResponse = chatState.isThinkingOrAnswering
	const { isSidebarCollapsed } = useAppContext()
	const { socket } = useGlobalContext()
	const [artifactContent, setArtifactContent] = useState<string>('')
	const [artifactName, setArtifactName] = useState<string>('')

	const socketResponse = useArtifactSocket({
		socket,
		chatId,
		artifactId: artifactParams.artifact.id
	})

	useEffect(() => {
		function updateArtifactContent() {
			if (artifactParams && socketResponse) {
				setArtifactContent(socketResponse.artifact.content)
				setArtifactName(socketResponse.artifact.name)
			}
		}

		updateArtifactContent()
	}, [socketResponse])

	const handleClose = () => {
		setIsClosing(true)

		if (artifactParams) {
			chatDispatch({ type: 'setArtifactParams', artifactParams: undefined })
			chatDispatch({ type: 'setReactPdfViewerParams', params: undefined })
			ApiCaller.track('Closed_artifact')
		}

		setTimeout(() => {
			setIsClosing(false)
		}, 300)
	}

	useEffect(() => {
		async function loadArtifactContent() {
			try {
				const loadedArtifact = await ApiCaller.getChatArtifact(chatId, artifactParams.artifact.id)

				setArtifactContent(loadedArtifact.content)
				setArtifactName(loadedArtifact.name)
			} catch (error) {
				captureException(error)
			}
		}

		loadArtifactContent()
	}, [artifactParams])

	if (!artifactParams) {
		return null
	}

	return (
		<ArtifactContext.Provider
			value={ {
				artifactParams,
				isProcessingResponse,
				artifactContent,
				onSourceSelected(params) {
					chatDispatch({ type: 'setReactPdfViewerParams', params })
				}
			} }>
			<div className={ ` fixed top-0 flex flex-col items-end right-0 mt-[50px] transition-all duration-500 ${isSidebarCollapsed ? 'w-1/2' : 'w-[calc(50%-130px)]'} h-full z-10` }>
				<div className={ `flex-1 overflow-hidden transition-[width] mb-[50px] ${isClosing ? 'shrink' : 'expand'}` }>
					<div className='rounded-l-xl h-full w-full flex flex-col overflow-hidden bg-zinc-50'>
						<div className='bg-zinc-200  rounded-tl-xl font-semibold flex items-center py-2 px-4 text-sm'>
							{ artifactName }
							<FontAwesomeIcon
								icon='times'
								className='ml-auto cursor-pointer'
								onClick={ () => {
									handleClose()
								} }
							/>
						</div>
						{ children }
					</div>
					{ children }
				</div>
			</div>
		</ArtifactContext.Provider>
	)
}

Artifact.Content = Content
Artifact.Footer = Footer

export function useArtifactContext() {
	const context = useContext(ArtifactContext)

	if (!context) {
		throw new Error('useArtifactContext must be used within an ArtifactProvider')
	}

	return context
}
